import { create } from 'zustand';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/core/supabase';

// Module-level variable for auth subscription cleanup
let _authSubscription: { unsubscribe: () => void } | null = null;

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;

  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, username: string) => Promise<{ error: string | null; needsConfirmation?: boolean }>;
  signOut: () => Promise<void>;
  /** 发送密码重置邮件 */
  resetPasswordForEmail: (email: string) => Promise<{ error: string | null }>;
  /** 使用 recovery token 后设置新密码 */
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  /** 重新发送确认邮件 */
  resendConfirmation: (email: string) => Promise<{ error: string | null }>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: false,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;

    // Listen for auth state changes
    if (_authSubscription) _authSubscription.unsubscribe();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      set({
        session,
        user: session?.user ?? null,
        initialized: true,
      });
    });
    _authSubscription = subscription;

    // Get initial session
    const { data } = await supabase.auth.getSession();
    set({
      session: data.session,
      user: data.session?.user ?? null,
      initialized: true,
    });
  },

  signIn: async (email, password) => {
    set({ loading: true });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error && data.session) {
      set({ loading: false, session: data.session, user: data.session.user });
    } else {
      set({ loading: false });
    }
    if (error) {
      // Friendly Chinese error messages
      const msg = error.message?.toLowerCase() ?? '';
      if (msg.includes('invalid login credentials')) {
        return { error: '邮箱或密码错误，请检查后重试' };
      }
      if (msg.includes('email not confirmed')) {
        return { error: '邮箱尚未验证，请检查收件箱或使用新邮箱注册' };
      }
      return { error: error.message };
    }
    return { error: null };
  },

  signUp: async (email, password, username) => {
    set({ loading: true });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username } },
    });

    // Handle "User already registered" — try signing in directly
    // (covers the case where user registered before email confirmation was disabled)
    if (error?.message?.toLowerCase().includes('already registered')) {
      const { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({ email, password });
      if (!signInError && signInData.session) {
        set({ loading: false, session: signInData.session, user: signInData.session.user });
        return { error: null, needsConfirmation: false };
      }
      set({ loading: false });
      return { error: '该邮箱已注册。如果是你的账号，请直接登录；如密码不同，请使用「忘记密码」找回。' };
    }

    if (!error && data.user) {
      // Create profile (ignore conflict if already exists)
      const { error: profileErr } = await supabase.from('profiles').upsert({
        id: data.user.id,
        username,
      }, { onConflict: 'id' });
      if (profileErr) console.warn('[Auth] Profile upsert failed:', profileErr.message);
      // Immediately update store (signUp with autoConfirm returns session)
      if (data.session) {
        set({ loading: false, session: data.session, user: data.session.user });
        return { error: null, needsConfirmation: false };
      } else {
        // Email confirmation required — no session yet
        set({ loading: false });
        return { error: null, needsConfirmation: true };
      }
    } else {
      set({ loading: false });
    }
    return { error: error?.message ?? null };
  },

  signOut: async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('[Auth] signOut network error:', (err as Error).message);
    }
    // Always clear local state regardless of server response
    set({ user: null, session: null });
  },

  resetPasswordForEmail: async (email) => {
    set({ loading: true });
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    set({ loading: false });
    return { error: error?.message ?? null };
  },

  updatePassword: async (newPassword) => {
    set({ loading: true });
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    set({ loading: false });
    return { error: error?.message ?? null };
  },

  resendConfirmation: async (email) => {
    set({ loading: true });
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    set({ loading: false });
    return { error: error?.message ?? null };
  },
}));
